import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { MessageService, PrimeNGConfig } from 'primeng/api';
import { FileUpload, FileUploadModule } from 'primeng/fileupload';
import { ButtonModule } from 'primeng/button';
import { CommonModule } from '@angular/common';
import { BadgeModule } from 'primeng/badge';
import { HttpClientModule } from '@angular/common/http';
import { ProgressBarModule } from 'primeng/progressbar';
import { ToastModule } from 'primeng/toast';
import { FormInputConfig } from '../../interfaces/form-input-config';
import { TranslateModule } from '@ngx-translate/core';
import { URLS } from '../../constants/URLS';
import { SkeletonModule } from 'primeng/skeleton';
import {
  handleApiErrors,
  createValidationError,
} from '../../../../common/core/data-access/http-client/interceptors/error.interceptor';
import { ErrorOrError } from '../../../../common/core/results/error-or/models/error-or-error.model';
import { ToastService } from '../toasters/Toast.service';
import { UserContextService, UtilityManagementPermissions } from '../../../../common';
import { RequestBaseService } from '../../services/http-base/request-base.service';

@Component({
  selector: 'app-drop-drag-files',
  standalone: true,
  imports: [
    FileUploadModule,
    ButtonModule,
    BadgeModule,
    SkeletonModule,
    ProgressBarModule,
    ToastModule,
    HttpClientModule,
    CommonModule,
    TranslateModule,
  ],
  providers: [MessageService],
  templateUrl: './drop-drag-files.component.html',
  styleUrl: './drop-drag-files.component.scss',
})
export class DropDragFilesComponent {
  @Input() fileSizeLimit: number = 2 * 1024 * 1024; // Set default to 2MB
  @Input() maxFiles: number = 1;
  @Input() keepFile: boolean = true;
  @Input() formItem!: FormInputConfig;
  @Input() customTemplate?: TemplateRef<any>;
  @Input() hint?: string;
  @Input() reset?: boolean = false;
  @Input() previewImg: boolean = false;
  @Input() cacheFiles?: boolean = false;
  private acceptTypes: string[] = ['*'];
  @Output() fileAdded = new EventEmitter<any>();
  @Output() listFilesIdsAdded = new EventEmitter<any>();
  @Output() fileRemoved = new EventEmitter<any>();
  @Output() onReset = new EventEmitter<any>();
  @Output() onTouched = new EventEmitter<any>();
  @Input() set accepts(value: string[]) {
    this.acceptTypes = value;
  }
  @Input() fileAddedBefore: any;

  get acceptsList(): string {
    return this.acceptTypes
      .map((type) => (type.startsWith('.') ? type : `.${type}`))
      .join(',');
  }

  filesListIds: any = [];
  allUploadedFiles: any = [];
  allSuccessUploadedFiles: any = [];
  files: File[] = [];
  totalSize: number = 0;
  index = 0;
  totalSizePercent: number = 0;
  isLoadingUploadFiles: boolean = false;

  hasCreateAttachmentsPermission: boolean = (UserContextService && UtilityManagementPermissions) ? UserContextService.hasPermission(UtilityManagementPermissions?.Attachments?.Create) : false;


  constructor(
    private config: PrimeNGConfig,
    private messageService: MessageService,
    private dynamicLoadDataService: RequestBaseService,
    private toastService: ToastService,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    this.fileAddedBefore?.forEach((element: any) => {
      this.allUploadedFiles.push({
        id: element.id,
        name: element.name,
        size: element.size,
      });
      this.filesListIds.push(element.id);
    });
    this.fileAdded.emit(this.filesListIds);
  }

  @ViewChild('fileUploader') fileUploader!: FileUpload;

  // Triggered when user clicks the upload button
  triggerFileUpload() {
    // if (!this.hasCreateAttachmentsPermission) {
    //   this.toastService.showError(
    //     'Permission Denied',
    //     'You do not have permission to upload files.',
    //     false,
    //     5000
    //   );
    //   return; // Stop further processing if permission is denied
    // }
    this.fileUploader.choose(); // Trigger the file input dialog
  }

  onFileSelect(event: any) {
    this.files = [...event.files]; // Store selected files

    const oversizedFiles = this.files.filter(
      (file) => file.size > this.fileSizeLimit
    );
    const validFiles = this.files.filter(
      (file) => file.size <= this.fileSizeLimit
    );

    if (oversizedFiles.length > 0) {
      const oversizedFileNames = oversizedFiles
        .map((file) => file.name)
        .join(', ');
      this.toastService.showWarning(
        'File Too Large',
        `The following file(s) exceed the size limit of ${(
          this.fileSizeLimit /
          (1024 * 1024)
        ).toFixed(2)} MB: ${oversizedFileNames}`,
        false,
        5000
      );
    }

    this.files = validFiles;

    // Check for duplicate files
    const duplicateFiles = this.files.filter((file) =>
      this.allSuccessUploadedFiles.some(
        (uploadedFile: any) =>
          uploadedFile.name === file.name && uploadedFile.size === file.size
      )
    );

    if (duplicateFiles.length > 0) {
      const duplicateNames = duplicateFiles.map((file) => file.name).join(', ');
      this.toastService.showError(
        'Duplicate File(s)',
        `The following file(s) have already been uploaded: ${duplicateNames}`,
        false,
        5000
      );
      return;
    }

    // Proceed to add and upload valid files
    for (let file of this.files) {
      this.allUploadedFiles.push(file);
      this.submitForm(file);
    }
  }

  removeFile(file: File, index: number) {
    this.allUploadedFiles = this.allUploadedFiles.filter(
      (f: any) => f.name !== file.name
    );
    this.files = this.files.filter((f) => f.name !== file.name);
    this.filesListIds.splice(index, 1);
    this.fileAdded.emit(this.filesListIds);
    this.cdr.detectChanges();
  }

  truncateFileName(name: string, maxLength: number = 15): string {
    if (name.length <= maxLength) {
      return name;
    }
    const extension = name.substring(name.lastIndexOf('.'));
    const baseName = name.substring(0, maxLength - extension.length);
    return `${baseName}...${extension}`;
  }

  async submitForm(data: any) {
    const formData = new FormData();
    formData.append('files', data);
    try {
      this.isLoadingUploadFiles = true;
      this.dynamicLoadDataService.setBody({ data: formData });
      const res = await this.dynamicLoadDataService.post({ path: URLS.ATTACHMENTS });
      this.allSuccessUploadedFiles.push(data);
      this.filesListIds.push(...res);
      this.fileAdded.emit(this.filesListIds);
    } catch (errors: ErrorOrError[] | unknown | any) {
      handleApiErrors(
        createValidationError(
          'Validation Error',
          `${errors[0]?.description}`,
          'file'
        ),
        this.toastService
      );
      this.allUploadedFiles = this.allUploadedFiles.filter(
        (file: any) => file.name !== data.name
      );
      this.cdr.detectChanges();
    } finally {
      this.isLoadingUploadFiles = false;
    }
  }

  onClearTemplatingUpload(clear: any) {
    clear();
    this.totalSize = 0;
    this.totalSizePercent = 0;
  }

  onSelectedFiles(event: any) {
    this.files = event.currentFiles;
    this.files.forEach((file) => {
      this.totalSize += parseInt(this.formatSizeUnits(file.size));
    });
    this.totalSizePercent = this.totalSize / 10;
  }

  formatSizeUnits(size: any): any {
    if (size >= 1073741824) {
      size = (size / 1073741824).toFixed(2) + ' GB';
    } else if (size >= 1048576) {
      size = (size / 1048576).toFixed(2) + ' MB';
    } else if (size >= 1024) {
      size = (size / 1024).toFixed(2) + ' KB';
    } else if (size > 1) {
      size = size + ' bytes';
    } else if (size == 1) {
      size = size + ' byte';
    } else {
      size = '0 bytes';
    }
    return size;
  }
}
